"use client";
import React, { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { CloseButton, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ToggleGroup } from "components/ToggleGroup";
import { Button, ButtonVariantTypes } from "components/ui/button";
import { Textarea } from "components/ui/textarea";

interface ImageActionsProps {
  onLike: () => void;
  onDislike: () => void;
}

const ImageActions: React.FC<ImageActionsProps> = ({ onLike, onDislike }) => {
  const [feedback, setFeedback] = useState("");
  const [popoverMessage, setPopoverMessage] = useState("");
  const [isLiked, setIsLiked] = useState<boolean | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const predefinedAnswers = ["Colors", "Layout", "Materials / Finishes", "Furniture", "Lighting"];

  const handleLike = () => {
    setIsLiked(true);
    setPopoverMessage("What do you like about it? (optional)");
    onLike();
  };

  const handleDislike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(false);
    onDislike();
  };

  const handleSave = () => {
    setFeedback("");
  };

  return (
    <div className="image-actions-container mb-2.5 flex justify-center space-x-4 font-matter">
      {/* Dislike Button */}
      <Button
        variant={!isLiked ? ButtonVariantTypes.SELECTED : ButtonVariantTypes.ACTION_NEGATIVE}
        size="rounded"
        onClick={handleDislike}
        className="rating-button dislike"
      >
        <ThumbsDown strokeWidth={1.8} />
      </Button>
      <Popover>
        <PopoverButton>
          <Button
            variant={isLiked === true ? ButtonVariantTypes.SELECTED : ButtonVariantTypes.ACTION_POSITIVE}
            size="rounded"
            onClick={handleLike}
            className="rating-button like"
          >
            <ThumbsUp strokeWidth={1.8} />
          </Button>
        </PopoverButton>
        <PopoverPanel anchor="bottom" className="z-50 w-96 rounded-lg bg-neutral-0 p-8 shadow-lg">
          <p className="mb-4 py-4  font-matter text-lg font-medium text-primary-700">{popoverMessage}</p>
          <ToggleGroup
            value={selectedOptions}
            onChange={(value) => setSelectedOptions(value as string[])}
            className="mb-4 flex flex-wrap gap-2 pb-4 font-matter"
            items={predefinedAnswers.map((answer) => ({
              value: answer,
              label: answer,
              position: "left",
              renderAs: "div",
              onlyDisplayLabel: true,
              className: `btnn bnbtransition overflow-hidden rounded-lg border border-base-primary px-4 py-2 text-sm font-medium hover:bg-offwhite lg:hover:shadow-[0_0_0_1px_rgb(0,0,0)]  ${
                selectedOptions.includes(answer)
                  ? "hover: bg-primary-700 text-white hover:bg-primary-700"
                  : "bg-white text-base-primary"
              }`,
              onChange: () => {} // This managed by the parent component
            }))}
          />
          <Textarea
            className="mb-6 min-h-[100px] font-matter"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Your thoughts..."
          />
          <CloseButton>
            <Button
              variant={
                selectedOptions.length > 0 || feedback.length > 0
                  ? ButtonVariantTypes.BRAND
                  : ButtonVariantTypes.SECONDARY
              }
              size="big"
              onClick={handleSave}
              disabled={selectedOptions.length === 0 && feedback.length === 0}
              className={`${selectedOptions.length === 0 && feedback.length === 0 ? "cursor-not-allowed" : ""}`}
            >
              Save
            </Button>
          </CloseButton>
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default ImageActions;
